/**
 * Per-capita sector GHG (inventory total ÷ population) vs California statewide average, same years as the sector chart.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';
import {
  CALIFORNIA_STATE_GEOFIPS,
  isCaliforniaStateInventoryLocation,
} from './californiaGhgInventoryVmtService';
import { fetchCaliforniaStateGhgBySector } from './stateGhgInventorySectorService';

export type SectorGhgPerCapitaYearPoint = {
  year: number;
  localPerCapitaMt: number | null;
  californiaPerCapitaMt: number | null;
};

/** Sum all sector dataset values at each year index (matches stacked total height). */
export function yearTotalsFromSectorChartData(chartData: {
  labels?: unknown[];
  datasets?: Array<{ data?: unknown[] }>;
}): Map<number, number> {
  const labels = chartData.labels ?? [];
  const datasets = chartData.datasets ?? [];
  const out = new Map<number, number>();
  labels.forEach((yRaw, idx) => {
    const year = Number(yRaw);
    if (!Number.isFinite(year)) return;
    let sum = 0;
    for (const ds of datasets) {
      const arr = ds.data;
      const v = Array.isArray(arr) ? Number(arr[idx]) : NaN;
      if (Number.isFinite(v)) sum += v;
    }
    out.set(year, sum);
  });
  return out;
}

async function populationByYear(
  client: SupabaseClient,
  geofips: string,
  geographyType: string,
  yearMin: number,
  yearMax: number
): Promise<Map<number, number>> {
  const { data, error } = await client
    .from('population')
    .select('year, population')
    .eq('geofips', geofips)
    .eq('geography_type', geographyType)
    .gte('year', yearMin)
    .lte('year', yearMax)
    .order('year', { ascending: true });

  if (error) {
    console.warn('sectorGhgPerCapitaComparisonService: population', error.message);
    return new Map();
  }
  const m = new Map<number, number>();
  for (const r of data ?? []) {
    const y = Number((r as { year?: unknown }).year);
    const p = Number((r as { population?: unknown }).population);
    if (Number.isFinite(y) && Number.isFinite(p) && p > 0) m.set(y, p);
  }
  return m;
}

async function californiaStateTotalEmissionsByYear(
  _client: SupabaseClient,
  yearMin: number,
  yearMax: number
): Promise<Map<number, number>> {
  const payload = await fetchCaliforniaStateGhgBySector({ yearMin, yearMax });
  const m = new Map<number, number>();
  for (const row of payload.rows) {
    if (row.total_emissions != null && Number.isFinite(row.total_emissions)) {
      m.set(row.year, row.total_emissions);
    }
  }
  return m;
}

async function californiaStatePopulationByYear(
  client: SupabaseClient,
  yearMin: number,
  yearMax: number
): Promise<Map<number, number>> {
  const runQuery = async (filters: { locationName?: string; geofips?: string | number }) => {
    let q = client
      .from('population')
      .select('year, population')
      .eq('geography_type', 'State')
      .gte('year', yearMin)
      .lte('year', yearMax)
      .order('year', { ascending: true });
    if (filters.locationName != null) {
      q = q.eq('location_name', filters.locationName);
    }
    if (filters.geofips != null) {
      q = q.eq('geofips', filters.geofips);
    }
    return q;
  };

  let { data, error } = await runQuery({ locationName: 'California' });
  if (error) {
    console.warn('sectorGhgPerCapitaComparisonService: CA population', error.message);
    return new Map();
  }
  if (!data?.length) {
    const second = await runQuery({ geofips: CALIFORNIA_STATE_GEOFIPS });
    if (!second.error && second.data?.length) {
      data = second.data;
    }
  }

  const m = new Map<number, number>();
  for (const r of data ?? []) {
    const y = Number((r as { year?: unknown }).year);
    const p = Number((r as { population?: unknown }).population);
    if (Number.isFinite(y) && Number.isFinite(p) && p > 0) m.set(y, p);
  }
  return m;
}

export async function fetchSectorGhgPerCapitaVsCalifornia(
  location: string,
  chartData: { labels?: unknown[]; datasets?: Array<{ data?: unknown[] }> } | null | undefined,
  client: SupabaseClient = supabaseClient
): Promise<{ points: SectorGhgPerCapitaYearPoint[]; skip: boolean; message?: string }> {
  if (!chartData?.labels?.length) {
    return { points: [], skip: true, message: 'No inventory chart data.' };
  }

  await supabaseLocationService.initialize().catch(() => {});
  const geofipsRaw = await supabaseLocationService.getGeofips(location);
  const geofipsNum = geofipsRaw != null ? Number(geofipsRaw) : NaN;
  if (isCaliforniaStateInventoryLocation(location, Number.isFinite(geofipsNum) ? geofipsNum : null)) {
    return {
      points: [],
      skip: true,
      message: 'Per-capita comparison is for cities and counties; California statewide is already the state total.',
    };
  }

  const geofipsCandidates = Array.from(
    new Set(
      [geofipsRaw, String(geofipsRaw).replace(/^0+/, '') || String(geofipsRaw)].filter(
        (x) => x != null && String(x).length > 0
      ).map((x) => String(x))
    )
  );

  let geographyType = 'City';
  let geofipsStr = geofipsCandidates[0] || '';
  try {
    let popMeta: { geography_type?: string }[] | null = null;
    for (const gCandidate of geofipsCandidates) {
      const { data } = await client
        .from('population')
        .select('geography_type')
        .eq('geofips', gCandidate)
        .eq('year', 2023)
        .limit(1);
      if (data?.length) {
        popMeta = data as { geography_type?: string }[];
        geofipsStr = gCandidate;
        break;
      }
    }
    const g = popMeta?.[0]?.geography_type;
    if (g && typeof g === 'string') geographyType = g;
  } catch {
    /* keep default */
  }
  if (!geofipsStr) {
    return { points: [], skip: true, message: 'Could not resolve geofips for this location.' };
  }

  const totals = yearTotalsFromSectorChartData(chartData);
  if (totals.size === 0) {
    return { points: [], skip: true, message: 'No year totals from inventory chart.' };
  }

  const years = Array.from(totals.keys()).sort((a, b) => a - b);
  const yMin = years[0];
  const yMax = years[years.length - 1];

  const [localPop, caEmissions, caPop] = await Promise.all([
    populationByYear(client, geofipsStr, geographyType, yMin, yMax),
    californiaStateTotalEmissionsByYear(client, yMin, yMax),
    californiaStatePopulationByYear(client, yMin, yMax),
  ]);

  const points: SectorGhgPerCapitaYearPoint[] = years.map((year) => {
    const totalMt = totals.get(year) ?? 0;
    const pop = localPop.get(year);
    const caMt = caEmissions.get(year);
    const caP = caPop.get(year);

    const localPerCapitaMt =
      pop != null && pop > 0 && Number.isFinite(totalMt) ? totalMt / pop : null;
    const californiaPerCapitaMt =
      caMt != null && caP != null && caP > 0 && Number.isFinite(caMt) ? caMt / caP : null;

    return { year, localPerCapitaMt, californiaPerCapitaMt };
  });

  return { points, skip: false };
}
